'use client';

import React, { useMemo } from 'react';
import { IconJusticeScale, IconLike } from '@repo/svgs';
import { Divider } from '@/components/ui/Divider';
import { ListCell } from '@/components/ui/ListCell';
import { useBuilding } from '../../../context/BuildingProvider';
import { formatDateReadable } from '@repo/utils';

interface LitigationRecord {
  caseType: string;
  date: string;
  status: string;
}

interface GroupedLitigation {
  year: string;
  records: LitigationRecord[];
}

const formatStatus = (status: string): string => {
  if (!status) return 'Closed';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const LitigationItem = ({ record, isLast }: { record: LitigationRecord; isLast?: boolean }) => (
  <>
    <ListCell
      leadingIcon={<IconJusticeScale className="w-6 h-6 text-caution" />}
      title={record.caseType}
      subtitle={record.date}
      trailingTitle={record.status}
      trailing="none"
    />
    {!isLast && (
      <div className="px-2">
        <Divider />
      </div>
    )}
  </>
);

export function LitigationModal() {
  const { building } = useBuilding();

  const groupedLitigations = useMemo(() => {
    const litigationsData = building?.openData?.litigations?.data;
    if (!litigationsData || typeof litigationsData !== 'object') return [];

    return Object.entries(litigationsData)
      .map(([year, records]) => ({
        year,
        records: (Array.isArray(records) ? records : []).map((record: any) => ({
          caseType: record.caseType || '-',
          date: record.caseOpenDate ? formatDateReadable(record.caseOpenDate) : '-',
          status: formatStatus(record.caseStatus || record.status || 'Closed'),
        })),
      }))
      .sort((a, b) => Number(b.year) - Number(a.year));
  }, [building]);

  const hasLitigations = groupedLitigations.length > 0;
  const litigationCount = building.openDataSummary?.numLitigations || 0;

  // Mock data for building vs NYC average (would come from API in real implementation)
  const buildingAverage = building?.openData?.litigations?.summary?.perUnitBuilding ?? 0.26;
  const nycAverage = building?.openData?.litigations?.summary?.perUnitCity ?? 0.81;
  const isBetterThanAverage = buildingAverage < nycAverage;

  const renderContent = () => {
    if (!hasLitigations) {
      return (
        <div className="bg-primary py-6 px-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 px-2">
              <IconJusticeScale className="w-8 h-8 text-secondary" />
              <p className="text-body-2 text-primary">
                There is no recorded housing litigation associated with this building from the past
                10 years.
              </p>
            </div>
            <div className="px-2">
              <div className="bg-secondary rounded-2xl p-4">
                <p className="text-body-2 text-secondary">
                  Litigation cases are pulled from NYC housing court records. This does not include
                  non-housing court cases, such as small-claims cases.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-primary py-4 px-4">
        <div className="flex flex-col gap-4">
          {/* Overview Section */}
          <div className="flex flex-col gap-4 px-2">
            {/* Success Badge */}
            {isBetterThanAverage && (
              <div className="bg-green-50 rounded-xl px-3 py-2 flex items-center gap-2">
                <IconLike className="w-4 h-4 text-success" />
                <p className="text-body-2 text-success flex-1">
                  Average litigation cases per unit for this building is better than the city
                  average.
                </p>
              </div>
            )}

            {/* Metrics Comparison */}
            <div className="flex items-center gap-4">
              <div className="flex-1 flex flex-col gap-2">
                <p className="text-caption text-secondary">Building average</p>
                <div className="flex flex-col">
                  <p className="text-heading-6 text-primary">{buildingAverage}</p>
                  <p className="text-body-2 text-primary">Litigation cases per unit</p>
                </div>
              </div>

              <div className="w-0 h-16 flex items-center justify-center">
                <div className="w-16 rotate-90">
                  <Divider />
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-2">
                <p className="text-caption text-secondary">NYC average</p>
                <div className="flex flex-col">
                  <p className="text-heading-6 text-primary">{nycAverage}</p>
                  <p className="text-body-2 text-primary">Litigation cases per unit</p>
                </div>
              </div>
            </div>
          </div>

          {/* Litigation Records List */}
          <div className="flex flex-col gap-1">
            {groupedLitigations.map(group => (
              <React.Fragment key={group.year}>
                <div className="bg-primary px-2 py-3 rounded-lg">
                  <p className="text-body-2-high text-primary">{group.year}</p>
                </div>
                {group.records.map((record, index) => (
                  <LitigationItem
                    key={`${group.year}-${index}`}
                    record={record}
                    isLast={index === group.records.length - 1}
                  />
                ))}
                {/* Add divider between years except for the last group */}
                {group !== groupedLitigations[groupedLitigations.length - 1] && (
                  <div className="px-2 py-0.5">
                    <Divider />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Info Container */}
          <div className="px-2">
            <div className="bg-secondary rounded-2xl p-4">
              <p className="text-body-2 text-secondary">
                Litigation cases are pulled from NYC housing court records. This does not include
                non-housing court cases, such as small-claims cases.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!hasLitigations) {
    return (
      <div className="flex flex-col gap-4 px-2">
        <IconJusticeScale className="w-8 h-8 text-secondary" />
        <p className="text-body-2 text-primary">
          There is no recorded housing litigation associated with this building from the past 10
          years.
        </p>
        <div className="bg-secondary rounded-2xl p-4">
          <p className="text-body-2 text-secondary">
            Litigation cases are pulled from NYC housing court records. This does not include
            non-housing court cases, such as small-claims cases.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Overview Section */}
      <div className="flex flex-col gap-4 px-2">
        {/* Success Badge */}
        {isBetterThanAverage && (
          <div className="bg-green-50 rounded-xl px-3 py-2 flex items-center gap-2">
            <IconLike className="w-4 h-4 text-success" />
            <p className="text-body-2 text-success flex-1">
              Average litigations per unit for this building is better than the city average.
            </p>
          </div>
        )}

        {/* Metrics Comparison */}
        <div className="flex items-center gap-4">
          <div className="flex-1 flex flex-col gap-2">
            <p className="text-caption text-secondary">Building average</p>
            <div className="flex flex-col">
              <p className="text-heading-6 text-primary">{buildingAverage}</p>
              <p className="text-body-2 text-primary">Litigations per unit</p>
            </div>
          </div>

          <div className="w-0 h-16 flex items-center justify-center">
            <div className="w-16 rotate-90">
              <Divider />
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-2">
            <p className="text-caption text-secondary">NYC average</p>
            <div className="flex flex-col">
              <p className="text-heading-6 text-primary">{nycAverage}</p>
              <p className="text-body-2 text-primary">Litigations per unit</p>
            </div>
          </div>
        </div>
      </div>

      {/* Litigation Records List */}
      <div className="flex flex-col gap-1">
        {groupedLitigations.map(group => (
          <React.Fragment key={group.year}>
            <div className="bg-primary px-2 py-3 rounded-lg">
              <p className="text-body-2-high text-primary">{group.year}</p>
            </div>
            {group.records.map((record, index) => (
              <LitigationItem
                key={`${group.year}-${index}`}
                record={record}
                isLast={index === group.records.length - 1}
              />
            ))}
            {/* Add divider between years except for the last group */}
            {group !== groupedLitigations[groupedLitigations.length - 1] && (
              <div className="px-2 py-0.5">
                <Divider />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Info Container */}
      <div className="px-2">
        <div className="bg-secondary rounded-2xl p-4">
          <p className="text-body-2 text-secondary">
            Litigation cases are pulled from NYC housing court records. This does not include
            non-housing court cases, such as small-claims cases.
          </p>
        </div>
      </div>
    </div>
  );
}
